import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import Base64Tool from '../components/Base64Tool';
import CodeBlock from '../components/CodeBlock';
import FaqSection from '../components/Faq';
import { PageHero } from '../components/InfoSections';
import { useTheme } from '../theme';
import { softwareApplicationSchema, faqPageSchema, breadcrumbSchema, Faq } from '../lib/schema';

const FAQS: Faq[] = [
  { q: 'How do I Base64 encode YAML?', a: 'Paste your YAML above and switch to Encode. This is exactly how Kubernetes Secrets and many CI/CD config systems store multi-line YAML or config values.' },
  { q: 'Why is YAML often Base64 encoded?', a: 'YAML is whitespace-sensitive, so embedding it inside JSON, environment variables or Kubernetes Secret manifests as-is is error-prone. Base64 turns it into a single safe text blob.' },
  { q: 'Does Base64 preserve YAML indentation?', a: 'Yes. Decoding returns the exact original bytes, including every space and newline, so indentation-sensitive YAML round-trips correctly.' },
  { q: 'Is this how Kubernetes Secrets work?', a: 'Yes — Kubernetes Secret data fields store Base64-encoded values. Note this is encoding, not encryption, so Secrets still need RBAC and encryption-at-rest for real protection.' },
];

export default function YamlToBase64Page() {
  const { themeClasses } = useTheme();
  return (
    <>
      <Seo
        title="YAML to Base64 — Encode & Decode YAML Online | EasyBase64"
        description="Encode YAML to Base64 and decode it back online for free. UTF-8 safe, instant, and fully private. Useful for Kubernetes Secrets and CI/CD config."
        path="/yaml-to-base64"
        jsonLd={[
          softwareApplicationSchema({ name: 'YAML to Base64 Converter', description: 'Encode and decode YAML as Base64 in the browser.', path: '/yaml-to-base64' }),
          faqPageSchema(FAQS),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'YAML to Base64', path: '/yaml-to-base64' }]),
        ]}
      />
      <PageHero
        title="YAML to Base64"
        subtitle={<>Encode YAML to Base64 and decode it back — the same encoding Kubernetes Secrets use. See also <Link to="/base64-database-storage" className="underline">storing data as Base64</Link>.</>}
      />
      <div className="mb-12">
        <Base64Tool initialMode="encode" variant="standard" />
      </div>
      <section className={`${themeClasses.infoCard} rounded-2xl p-5 sm:p-8 border mb-12 max-w-3xl mx-auto`}>
        <h2 className={`text-2xl font-bold mb-4 ${themeClasses.textPrimary}`}>Example</h2>
        <p className={`${themeClasses.textSecondary} leading-relaxed mb-4`}>
          The YAML <code>name: Ada</code> encodes to <code>bmFtZTogQWRh</code>. In code you would do:
        </p>
        <CodeBlock
          language="JavaScript"
          code={`const yaml = "name: Ada";
const b64 = btoa(unescape(encodeURIComponent(yaml))); // "bmFtZTogQWRh"
const back = decodeURIComponent(escape(atob(b64)));`}
        />
      </section>
      <FaqSection title="YAML to Base64 — FAQ" faqs={FAQS} />
    </>
  );
}
